import { Injectable } from '@angular/core';
import { FeatureFlagService } from '@core/services/statsig';
import { CiamV6NudgeConfig } from '@core/services/ciam-flags.service';
import {
  CIAM_NUDGE_BUDGET_EXHAUSTED_KEY,
  CIAM_NUDGE_DISMISSAL_COUNT_KEY,
  CIAM_NUDGE_LAST_DISMISSED_KEY,
  JIT_MIGRATION_LAST_SHOWN_KEY
} from '@core/constants/storage-keys';

/**
 * Event and metadata names are a cross-platform contract shared with CiamV6Gates.cs on MAUI.
 * Renaming one side splits the data in the Statsig console.
 */
export const NUDGE_EVENTS = {
  shown: 'ciam_nudge_shown',
  dismissed: 'ciam_nudge_dismissed',
  ctaTapped: 'ciam_nudge_cta_tapped',
  migrationCompleted: 'ciam_nudge_migration_completed',
  suppressed: 'ciam_nudge_suppressed',
  /** A hard-blocked user left the OTP flow. Records no state; without it the journey ends in silence. */
  mandatoryAbandoned: 'ciam_nudge_mandatory_abandoned'
} as const;

export const NUDGE_TRIGGER = {
  secondaryCta: 'secondary_cta',
  /** Web only. MAUI's prompt has no close affordance. */
  modalClose: 'modal_close',
  abandonedOtp: 'abandoned_otp'
} as const;

export const NUDGE_SUPPRESSION_REASON = {
  cooldown: 'cooldown',
  cancelled: 'cancelled',
  /** Web only. MAUI has no equivalent: this describes .COM's single modal host. */
  modalBusy: 'modal_busy'
} as const;

/**
 * How the customer arrived at a migrated state. Both routes clear the ladder, but only one is a
 * conversion the nudge earned; without this the funnel counts migrations made elsewhere as its own.
 */
export const NUDGE_COMPLETION_SOURCE = {
  otpSuccess: 'otp_success',
  alreadyMigrated: 'already_migrated'
} as const;

/** Which client reported the event. MAUI sends "ios" or "android" from the same metadata key. */
export const NUDGE_PLATFORM = 'web';

export interface NudgeDisplayDecision {
  canShow: boolean;
  isMandatory: boolean;
  suppressionReason?: string;
}

const HIDDEN: NudgeDisplayDecision = { canShow: false, isMandatory: false };

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;

/**
 * Days is the correct fallback direction. A typo such as "Day" then produces cooldowns that are too
 * long, which is harmless; defaulting to minutes would pester production users over the same typo.
 */
function toMilliseconds(value: number, unit: string): number {
  switch ((unit ?? '').trim().toLowerCase()) {
    case 'minutes':
      return value * MS_PER_MINUTE;
    case 'hours':
      return value * MS_PER_HOUR;
    default:
      return value * MS_PER_DAY;
  }
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    //storage can be unavailable in private browsing; a nudge that repeats beats a page that throws
  }
}

function removeStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    //see writeStorage
  }
}

/**
 * Owns the dismissal ladder: how many times this browser has dismissed the nudge, when it last
 * happened, and whether the budget is spent. State is per browser and not scoped per user, matching
 * MAUI. Statsig never writes these values, it only changes what they are compared against.
 */
@Injectable()
export class MigrationNudgeStateService {
  constructor(private readonly featureFlags: FeatureFlagService) {
    this.migrateLegacyState();
  }

  get dismissalCount(): number {
    const raw = readStorage(CIAM_NUDGE_DISMISSAL_COUNT_KEY);
    const count = raw == null ? 0 : parseInt(raw, 10);
    return Number.isFinite(count) && count > 0 ? count : 0;
  }

  get lastDismissedAt(): number | null {
    const raw = readStorage(CIAM_NUDGE_LAST_DISMISSED_KEY);
    if (raw == null) {
      return null;
    }
    const ms = parseInt(raw, 10);
    return Number.isFinite(ms) && ms > 0 ? ms : null;
  }

  get budgetExhausted(): boolean {
    return readStorage(CIAM_NUDGE_BUDGET_EXHAUSTED_KEY) === 'true';
  }

  /**
   * Pure. Implements steps 2 through 6 of the shared algorithm; base eligibility stays in
   * JitMigrationService.isEligibleForNudge. Writes nothing so it is safe to call speculatively.
   */
  evaluate(config: CiamV6NudgeConfig): NudgeDisplayDecision {
    const cooldowns = config?.dismissalCooldowns;
    if (!cooldowns || cooldowns.length === 0) {
      return HIDDEN;
    }

    const count = this.dismissalCount;
    if (count <= 0) {
      return { canShow: true, isMandatory: false };
    }

    //the clamp is what makes the final entry repeat forever, which is how mandatory_enabled: false
    //keeps the nudge dismissible on a fixed interval instead of running off the end of the array
    const index = Math.min(count - 1, cooldowns.length - 1);
    const cooldownMs = toMilliseconds(cooldowns[index], config.dismissalCooldownUnit);

    const lastDismissedAt = this.lastDismissedAt;
    if (lastDismissedAt != null && Date.now() - lastDismissedAt < cooldownMs) {
      return { canShow: false, isMandatory: false, suppressionReason: NUDGE_SUPPRESSION_REASON.cooldown };
    }

    //the cooldown above is deliberately checked first: going mandatory before the last entry elapses
    //would make that entry dead code and hard-block the user with no warning
    const exhausted = this.budgetExhausted || count >= cooldowns.length;
    return { canShow: true, isMandatory: exhausted && config.mandatoryEnabled };
  }

  recordShown(config: CiamV6NudgeConfig, placement: string, isMandatory: boolean): void {
    const count = this.dismissalCount;
    const cooldowns = config?.dismissalCooldowns;

    //never brand a user as exhausted off a config that failed to load
    if (cooldowns && cooldowns.length > 0 && !this.budgetExhausted && count >= cooldowns.length) {
      //recorded even when mandatory_enabled is off: the flag captures that the budget was spent, the
      //config decides whether the app acts on it
      writeStorage(CIAM_NUDGE_BUDGET_EXHAUSTED_KEY, 'true');
    }

    this.logNudgeEvent(NUDGE_EVENTS.shown, count, {
      placement,
      is_mandatory: String(isMandatory),
      dismissal_count: String(count)
    });
  }

  recordCtaTapped(placement: string, isMandatory: boolean): void {
    const count = this.dismissalCount;
    this.logNudgeEvent(NUDGE_EVENTS.ctaTapped, count, {
      placement,
      is_mandatory: String(isMandatory),
      dismissal_count: String(count)
    });
  }

  recordDismissal(config: CiamV6NudgeConfig, placement: string, trigger: string): void {
    const count = this.dismissalCount + 1;
    const now = Date.now();

    writeStorage(CIAM_NUDGE_DISMISSAL_COUNT_KEY, String(count));
    writeStorage(CIAM_NUDGE_LAST_DISMISSED_KEY, String(now));

    //exhaustion is deliberately not persisted here, only on display, so growing the array before the
    //next showing still hands the user the extra dismissals it promises

    const cooldowns = config?.dismissalCooldowns;
    const cooldownApplied =
      cooldowns && cooldowns.length > 0 ? cooldowns[Math.min(count - 1, cooldowns.length - 1)] : 0;

    this.logNudgeEvent(NUDGE_EVENTS.dismissed, count, {
      placement,
      dismissal_count: String(count),
      last_dismissed_utc: new Date(now).toISOString(),
      cooldown_applied: String(cooldownApplied),
      cooldown_unit: config?.dismissalCooldownUnit,
      trigger
    });

    this.refreshStatsigUser();
  }

  recordSuppressed(placement: string, reason: string): void {
    this.logNudgeEvent(NUDGE_EVENTS.suppressed, undefined, { placement, reason });
  }

  /**
   * Sends whatever is queued immediately. Events are normally batched, so anything logged as the page
   * is being torn down would go nowhere without this.
   */
  flushEvents(): void {
    try {
      this.featureFlags.flush();
    } catch {
      //telemetry must never break the nudge
    }
  }

  recordMandatoryAbandoned(placement: string): void {
    const count = this.dismissalCount;
    this.logNudgeEvent(NUDGE_EVENTS.mandatoryAbandoned, count, {
      placement,
      dismissal_count: String(count)
    });
  }

  clearState(placement: string, source: string): void {
    const count = this.dismissalCount;
    const hasState = count > 0 || this.budgetExhausted || this.lastDismissedAt != null;

    //a completed OTP always reports: someone who accepts the first showing never dismissed, so there is
    //no state to find and the funnel would drop exactly the customers who converted fastest. The
    //self-heal has no such proof and runs on any page load, so it stays silent for the many who never
    //saw a nudge
    const isOtpSuccess = source === NUDGE_COMPLETION_SOURCE.otpSuccess;
    if (!hasState && !isOtpSuccess) {
      return;
    }

    this.logNudgeEvent(NUDGE_EVENTS.migrationCompleted, count, {
      placement,
      dismissal_count: String(count),
      source
    });

    //nothing stored means nothing to tear down, and skipping keeps the writes and user refresh off the
    //first-time converter's path exactly as before
    if (!hasState) {
      return;
    }

    removeStorage(CIAM_NUDGE_DISMISSAL_COUNT_KEY);
    removeStorage(CIAM_NUDGE_LAST_DISMISSED_KEY);
    removeStorage(CIAM_NUDGE_BUDGET_EXHAUSTED_KEY);

    this.refreshStatsigUser();
  }

  /**
   * Seeds the ladder from the retired 24-hour scheme so anyone who dismissed yesterday is not
   * re-prompted the moment this deploys.
   */
  private migrateLegacyState(): void {
    const legacy = readStorage(JIT_MIGRATION_LAST_SHOWN_KEY);
    if (legacy == null) {
      return;
    }
    removeStorage(JIT_MIGRATION_LAST_SHOWN_KEY);

    if (readStorage(CIAM_NUDGE_DISMISSAL_COUNT_KEY) != null) {
      return;
    }

    const ms = parseInt(legacy, 10);
    if (!Number.isFinite(ms) || ms <= 0) {
      return;
    }

    writeStorage(CIAM_NUDGE_DISMISSAL_COUNT_KEY, '1');
    writeStorage(CIAM_NUDGE_LAST_DISMISSED_KEY, String(ms));
  }

  /**
   * Sends the count as the event value as well as metadata. Metadata is a string record, so a count
   * only stored there is filterable but not chartable.
   */
  private logNudgeEvent(eventName: string, value: number | undefined, metadata: Record<string, string>): void {
    try {
      metadata.platform = NUDGE_PLATFORM;

      const clean: Record<string, string> = {};
      Object.keys(metadata).forEach((key) => {
        const entry = metadata[key];
        if (entry != null && entry !== '') {
          clean[key] = entry;
        }
      });
      this.featureFlags.logEvent(eventName, value, clean);
    } catch {
      //telemetry must never break the nudge
    }
  }

  private refreshStatsigUser(): void {
    try {
      //rebuilds the Statsig user from the last known Redux user, so the custom properties do not stay
      //stale until the next page load. updateUser() is not usable here: its argument is optional but
      //unconditionally assigned, so calling it with none would clear the signed-in user
      this.featureFlags.refreshUser();
    } catch {
      //stale properties are tolerable; a thrown exception here is not
    }
  }
}
